<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import PlatformAnnouncementsAPI from '../../api/platformAnnouncements';

const emit = defineEmits(['close']);
const store = useStore();
const router = useRouter();
const panelRef = ref(null);
const hasMarkedViewed = ref(false);
const expandedAnnouncements = ref(new Set());
const overflowingAnnouncements = ref(new Set());

function isExpanded(id) {
  return expandedAnnouncements.value.has(id);
}

function isOverflowing(id) {
  return overflowingAnnouncements.value.has(id);
}

function toggleExpand(id) {
  const newSet = new Set(expandedAnnouncements.value);
  if (newSet.has(id)) {
    newSet.delete(id);
  } else {
    newSet.add(id);
  }
  expandedAnnouncements.value = newSet;
}

function checkOverflow() {
  nextTick(() => {
    const els = panelRef.value?.querySelectorAll('[data-announcement-msg]') || [];
    const newSet = new Set();
    els.forEach(el => {
      const id = Number(el.dataset.announcementMsg);
      if (el.scrollHeight > el.clientHeight + 2) {
        newSet.add(id);
      }
    });
    overflowingAnnouncements.value = newSet;
  });
}

const announcements = computed(
  () => store.getters['platformAnnouncements/getAnnouncements']
);

const isFetching = computed(
  () => store.getters['platformAnnouncements/getUIFlags']?.isFetching
);

const typeConfig = {
  feature: { emoji: '🆕', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300', label: 'Novidade' },
  warning: { emoji: '⚠️', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300', label: 'Aviso' },
  billing: { emoji: '💳', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300', label: 'Cobrança' },
  usage_limit: { emoji: '📊', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300', label: 'Limite' },
  maintenance: { emoji: '🔧', color: 'bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300', label: 'Manutenção' },
  urgent: { emoji: '🚨', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300', label: 'Urgente' },
};

function getConfig(type) {
  return typeConfig[type] || typeConfig.feature;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (hours < 1) return 'Agora';
  if (hours < 24) return `${hours}h atrás`;
  if (days < 7) return `${days}d atrás`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

async function dismissAnnouncement(id) {
  try {
    await store.dispatch('platformAnnouncements/dismiss', id);
  } catch (error) {
    // Error handled in store action
  }
}

function openAction(url) {
  if (!url) return;

  // Internal platform URLs: navigate via Vue Router to stay within the SPA.
  // This avoids full page reloads that cause auth race conditions and redirects.
  try {
    const parsed = new URL(url, window.location.origin);
    const isInternal = parsed.origin === window.location.origin
      && parsed.pathname.startsWith('/app/');
    if (isInternal) {
      router.push(parsed.pathname);
      emit('close');
      return;
    }
  } catch {
    // If URL parsing fails, fall through to window.open
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

function handleClickOutside(event) {
  if (panelRef.value && !panelRef.value.contains(event.target)) {
    // Check if click is on the bell button itself
    const bellButton = panelRef.value?.parentElement?.querySelector('button');
    if (bellButton && bellButton.contains(event.target)) return;
    emit('close');
  }
}

// v6.4.49: Mark announcements as viewed when panel opens (fire-and-forget)
function markAnnouncementsAsViewed() {
  if (hasMarkedViewed.value) return;

  const items = announcements.value || [];
  const ids = items.map(a => a.id);
  if (ids.length > 0) {
    hasMarkedViewed.value = true;
    PlatformAnnouncementsAPI.markViewed(ids).catch(() => {
      // Silent failure — view tracking should not disrupt UX
      // Reset flag so it can retry on next panel open
      hasMarkedViewed.value = false;
    });
  }
}

// Watch for announcements to be loaded (they may arrive after mount via async fetch)
watch(announcements, (newVal) => {
  if (newVal?.length > 0 && !hasMarkedViewed.value) {
    markAnnouncementsAsViewed();
  }
  if (newVal?.length > 0) {
    checkOverflow();
  }
}, { immediate: true });

onMounted(() => {
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
  }, 100);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div
    ref="panelRef"
    class="absolute top-10 left-0 z-50 w-80 max-h-96 bg-white dark:bg-n-solid-3 border border-n-weak rounded-xl shadow-2xl overflow-hidden flex flex-col"
  >
    <!-- Header -->
    <div
      class="px-4 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center justify-between flex-shrink-0"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-megaphone size-4 text-n-slate-11" />
        <span class="text-sm font-semibold text-n-slate-12">
          {{ $t('PLATFORM_ANNOUNCEMENTS.PANEL_TITLE') }}
        </span>
      </div>
      <button
        class="size-6 rounded-md hover:bg-n-alpha-2 grid place-content-center"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-3.5 text-n-slate-11" />
      </button>
    </div>

    <!-- Content -->
    <div class="overflow-y-auto flex-grow">
      <!-- Loading -->
      <div v-if="isFetching" class="flex items-center justify-center py-8">
        <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-9" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!announcements.length"
        class="text-center py-8 px-4"
      >
        <span class="i-lucide-bell-off size-8 text-n-slate-7 mx-auto block mb-2" />
        <p class="text-sm text-n-slate-9">
          {{ $t('PLATFORM_ANNOUNCEMENTS.EMPTY') }}
        </p>
      </div>

      <!-- Announcements list -->
      <div v-else class="divide-y divide-n-weak">
        <div
          v-for="announcement in announcements"
          :key="announcement.id"
          class="p-3 hover:bg-n-alpha-1 transition-colors"
        >
          <!-- Type badge + date -->
          <div class="flex items-center justify-between mb-1.5">
            <span
              :class="['text-[11px] px-1.5 py-0.5 rounded-full font-medium', getConfig(announcement.announcement_type).color]"
            >
              {{ getConfig(announcement.announcement_type).emoji }}
              {{ getConfig(announcement.announcement_type).label }}
            </span>
            <span class="text-[10px] text-n-slate-9">
              {{ formatDate(announcement.created_at) }}
            </span>
          </div>

          <!-- Title -->
          <h4 class="text-sm font-medium text-n-slate-12 mb-1 leading-tight">
            {{ announcement.title }}
          </h4>

          <!-- Message (expandable) -->
          <p
            :data-announcement-msg="announcement.id"
            class="text-[13px] text-n-slate-10 dark:text-n-slate-11 dark:font-medium mb-1 leading-relaxed transition-all duration-200"
            :class="{ 'line-clamp-4': !isExpanded(announcement.id) }"
          >
            {{ announcement.message }}
          </p>
          <button
            v-if="isOverflowing(announcement.id) || isExpanded(announcement.id)"
            class="flex items-center gap-0.5 text-[11px] text-n-slate-9 hover:text-n-brand mb-2 transition-colors cursor-pointer"
            @click="toggleExpand(announcement.id)"
          >
            <span
              class="size-3.5 transition-transform duration-200"
              :class="[
                isExpanded(announcement.id) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
              ]"
            />
            {{ isExpanded(announcement.id) ? 'Ver menos' : 'Ver mais' }}
          </button>

          <!-- Actions -->
          <div class="flex items-center gap-2">
            <button
              v-if="announcement.action_url"
              class="text-[11px] px-2 py-1 rounded-md bg-n-brand text-white hover:opacity-90 transition-opacity flex items-center gap-1"
              @click="openAction(announcement.action_url)"
            >
              <span class="i-lucide-external-link size-3" />
              {{ announcement.action_label || $t('PLATFORM_ANNOUNCEMENTS.VIEW_MORE') }}
            </button>
            <button
              class="text-[11px] px-2 py-1 rounded-md border border-n-weak text-n-slate-10 hover:bg-n-alpha-1 transition-colors"
              @click="dismissAnnouncement(announcement.id)"
            >
              {{ $t('PLATFORM_ANNOUNCEMENTS.DISMISS') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
